import { configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { useSelector, type TypedUseSelectorHook } from 'react-redux';
import { createEpicMiddleware } from 'redux-observable';

import organizationsEpics from './organizations/epics';
import reducer from './reducer';
import type { RootState } from './types';
import userEpics from './user/epics';
import usersEpics from './users/epics';

const sentryReduxEnhancer = Sentry.createReduxEnhancer() as unknown;

const createReduxStore = (state: Partial<RootState> = {}): ReturnType<typeof configureStore> => {
  const epicMiddleware = createEpicMiddleware();

  const store = configureStore({
    reducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({ serializableCheck: false }).concat(
        epicMiddleware
      ) as unknown as ReturnType<typeof getDefaultMiddleware>,
    preloadedState: { ...state } as RootState,
    enhancers: getDefaultEnhancers =>
      getDefaultEnhancers().concat(sentryReduxEnhancer) as unknown as ReturnType<
        typeof getDefaultEnhancers
      >,
  });

  epicMiddleware.run(userEpics);
  epicMiddleware.run(usersEpics);
  epicMiddleware.run(organizationsEpics);

  return store;
};

// Exports a singleton store for use from non-React modules (loaders)
type AppStore = ReturnType<typeof configureStore>;
const store: AppStore = createReduxStore();
export { store };

// State selector: Define the RootState type based on the store's state
export type { RootState };
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default createReduxStore;
